/**
 * Runtime contract for the map element's multi-select mode (`selection`).
 */
import {
  assertArray,
  assertNonEmptyString,
  assertRecord,
  rejectUnknownKeys,
  validateOptionalString,
  validateStringArray,
} from './elementFields.validation';
import { MULTI_SELECT_MAP_SLOT_KEYS } from '@/shared/config/mapSlotRegistry';

const MAP_SELECTION_LABEL_KEYS = [
  'list',
  'count',
  'selectAll',
  'selectNone',
  'lasso',
  'lassoActive',
] as const;

/**
 * Multi-select mode needs a live slot whose renderer honours it, a non-empty
 * set of choosable features with unique ids, and a chosen set drawn only from
 * those features: a selected id the owner cannot see or untick would be a
 * choice the page carries silently.
 */
export function validateMapSelection(value: unknown, figure: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(value, ['features', 'selectedIds', 'onSelectionChange', 'labels'], label);
  const slotKey =
    figure && typeof figure === 'object' && (figure as Record<string, unknown>).kind === 'live'
      ? (figure as Record<string, unknown>).slotKey
      : undefined;
  if (typeof slotKey !== 'string' || !MULTI_SELECT_MAP_SLOT_KEYS.has(slotKey)) {
    throw new Error(
      `Invalid descriptor: ${label} needs a live figure on a multi-select map slot (${[
        ...MULTI_SELECT_MAP_SLOT_KEYS,
      ].join(', ')}).`,
    );
  }
  assertArray(value.features, `${label}.features`);
  if (value.features.length === 0) {
    throw new Error(`Invalid descriptor: ${label}.features must name at least one feature.`);
  }
  const featureIds = new Set<string>();
  value.features.forEach((feature, index) => {
    const featureLabel = `${label}.features[${index}]`;
    assertRecord(feature, featureLabel);
    rejectUnknownKeys(feature, ['id', 'label', 'detail'], featureLabel);
    assertNonEmptyString(feature.id, `${featureLabel}.id`);
    assertNonEmptyString(feature.label, `${featureLabel}.label`);
    validateOptionalString(feature, 'detail', featureLabel);
    if (featureIds.has(feature.id)) {
      throw new Error(`Invalid descriptor: ${featureLabel}.id must be unique.`);
    }
    featureIds.add(feature.id);
  });
  validateStringArray(value.selectedIds, `${label}.selectedIds`);
  const selected = value.selectedIds as readonly string[];
  if (new Set(selected).size !== selected.length) {
    throw new Error(`Invalid descriptor: ${label}.selectedIds must not repeat an id.`);
  }
  const stray = selected.find((id) => !featureIds.has(id));
  if (stray !== undefined) {
    throw new Error(
      `Invalid descriptor: ${label}.selectedIds names "${stray}", which is not one of its features.`,
    );
  }
  if (typeof value.onSelectionChange !== 'function') {
    throw new Error(`Invalid descriptor: ${label}.onSelectionChange must be a function.`);
  }
  assertRecord(value.labels, `${label}.labels`);
  rejectUnknownKeys(value.labels, MAP_SELECTION_LABEL_KEYS, `${label}.labels`);
  MAP_SELECTION_LABEL_KEYS.forEach((key) =>
    assertNonEmptyString((value.labels as Record<string, unknown>)[key], `${label}.labels.${key}`),
  );
  const count = (value.labels as Record<string, string>).count;
  if (!count.includes('{selected}') || !count.includes('{total}')) {
    throw new Error(
      `Invalid descriptor: ${label}.labels.count must carry the {selected} and {total} placeholders.`,
    );
  }
}
